import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import GUI from "lil-gui";
import gsap from "gsap";
import particlesVertexShader from "./shaders/particles/vertex.glsl";
import particlesFragmentShader from "./shaders/particles/fragment.glsl";

/**
 * Base
 */
// Debug
const gui = new GUI({ width: 340 });
const debugObject = {};

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

// Loaders
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("./draco/");
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
  pixelRatio: Math.min(window.devicePixelRatio, 2),
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;
  sizes.pixelRatio = Math.min(window.devicePixelRatio, 2);

  // Materials
  if (particles)
    particles.material.uniforms.uResolution.value.set(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio);

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(sizes.pixelRatio);
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100);
camera.position.set(0, 0, 8 * 2);
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
});

renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(sizes.pixelRatio);

debugObject.clearColor = "#160920";
gui.addColor(debugObject, "clearColor").onChange(() => {
  renderer.setClearColor(debugObject.clearColor);
});
renderer.setClearColor(debugObject.clearColor);

/**
 * Displacement
 */
const displacement = {};
displacement.canvas = document.createElement("canvas");
displacement.canvas.width = 256;
displacement.canvas.height = 256;
displacement.canvas.style.position = "fixed";
displacement.canvas.style.width = "256px";
displacement.canvas.style.height = "256px";
displacement.canvas.style.top = 0;
displacement.canvas.style.left = 0;
displacement.canvas.style.zIndex = 10;
displacement.canvas.style.pointerEvents = "none";
displacement.context = displacement.canvas.getContext("2d");
displacement.context.fillRect(0, 0, displacement.canvas.width, displacement.canvas.height);

// Glow image (create a programmatic glow instead of loading an image)
displacement.glowImage = document.createElement("canvas");
displacement.glowImage.width = 64;
displacement.glowImage.height = 64;
const glowContext = displacement.glowImage.getContext("2d");
const gradient = glowContext.createRadialGradient(32, 32, 0, 32, 32, 32);
gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
glowContext.fillStyle = gradient;
glowContext.fillRect(0, 0, 64, 64);

document.body.append(displacement.canvas);

// Interactive plane
displacement.interactivePlane = new THREE.Mesh(
  new THREE.PlaneGeometry(10, 10),
  new THREE.MeshBasicMaterial({ color: "red", transparent: true, opacity: 0 })
);
displacement.interactivePlane.position.z = 0;
displacement.interactivePlane.visible = false;
scene.add(displacement.interactivePlane);

// Raycaster
displacement.raycaster = new THREE.Raycaster();

// Coordinates
displacement.screenCursor = new THREE.Vector2(9999, 9999);
displacement.canvasCursor = new THREE.Vector2(9999, 9999);
displacement.canvasCursorPrevious = new THREE.Vector2(9999, 9999);

// Mouse tracking
window.addEventListener("pointermove", (event) => {
  displacement.screenCursor.x = (event.clientX / sizes.width) * 2 - 1;
  displacement.screenCursor.y = -(event.clientY / sizes.height) * 2 + 1;
});

// Displacement texture
displacement.texture = new THREE.CanvasTexture(displacement.canvas);

// Debug
debugObject.showDisplacementCanvas = true;
gui.add(debugObject, "showDisplacementCanvas").onChange(() => {
  displacement.canvas.style.display = debugObject.showDisplacementCanvas ? "block" : "none";
});

/**
 * Particles
 */
let particles = null;

gltfLoader.load("./models.glb", (gltf) => {
  particles = {};
  particles.index = 0;

  // Positions
  const positions = gltf.scene.children.map((child) => child.geometry.attributes.position);

  particles.maxCount = 0;
  for (const position of positions) {
    if (position.count > particles.maxCount) particles.maxCount = position.count;
  }

  particles.positions = [];
  for (const position of positions) {
    const originalArray = position.array;
    const newArray = new Float32Array(particles.maxCount * 3);

    for (let i = 0; i < particles.maxCount; i++) {
      const i3 = i * 3;

      if (i3 < originalArray.length) {
        newArray[i3 + 0] = originalArray[i3 + 0];
        newArray[i3 + 1] = originalArray[i3 + 1];
        newArray[i3 + 2] = originalArray[i3 + 2];
      } else {
        const randomIndex = Math.floor(position.count * Math.random()) * 3;
        newArray[i3 + 0] = originalArray[randomIndex + 0];
        newArray[i3 + 1] = originalArray[randomIndex + 1];
        newArray[i3 + 2] = originalArray[randomIndex + 2];
      }
    }

    particles.positions.push(new THREE.Float32BufferAttribute(newArray, 3));
  }

  // Geometry
  const sizesArray = new Float32Array(particles.maxCount);

  for (let i = 0; i < particles.maxCount; i++) sizesArray[i] = Math.random();

  // Add angle attribute for displacement
  const anglesArray = new Float32Array(particles.maxCount);
  for (let i = 0; i < particles.maxCount; i++) anglesArray[i] = Math.random() * Math.PI * 2;

  particles.geometry = new THREE.BufferGeometry();
  particles.geometry.setAttribute("position", particles.positions[particles.index]);
  particles.geometry.setAttribute("aPositionTarget", particles.positions[3]);
  particles.geometry.setAttribute("aSize", new THREE.BufferAttribute(sizesArray, 1));
  particles.geometry.setAttribute("aAngle", new THREE.BufferAttribute(anglesArray, 1));

  // Material
  particles.colorA = "#ff7300";
  particles.colorB = "#0091ff";

  particles.material = new THREE.ShaderMaterial({
    vertexShader: particlesVertexShader,
    fragmentShader: particlesFragmentShader,
    uniforms: {
      uSize: new THREE.Uniform(0.4),
      uResolution: new THREE.Uniform(
        new THREE.Vector2(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)
      ),
      uProgress: new THREE.Uniform(0),
      uColorA: new THREE.Uniform(new THREE.Color(particles.colorA)),
      uColorB: new THREE.Uniform(new THREE.Color(particles.colorB)),
      uDisplacementTexture: new THREE.Uniform(displacement.texture),
    },
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  // Points
  particles.points = new THREE.Points(particles.geometry, particles.material);
  particles.points.frustumCulled = false;
  scene.add(particles.points);

  // Methods
  particles.morph = (index) => {
    // Update attributes
    particles.geometry.attributes.position = particles.positions[particles.index];
    particles.geometry.attributes.aPositionTarget = particles.positions[index];

    // Animate uProgress
    gsap.fromTo(particles.material.uniforms.uProgress, { value: 0 }, { value: 1, duration: 3, ease: "linear" });

    // Save index
    particles.index = index;
  };

  // Tweaks
  gui.addColor(particles, "colorA").onChange(() => {
    particles.material.uniforms.uColorA.value.set(particles.colorA);
  });
  gui.addColor(particles, "colorB").onChange(() => {
    particles.material.uniforms.uColorB.value.set(particles.colorB);
  });
  gui.add(particles.material.uniforms.uProgress, "value").min(0).max(1).step(0.001).name("uProgress").listen();

  particles.morph0 = () => {
    particles.morph(0);
  };
  particles.morph1 = () => {
    particles.morph(1);
  };
  particles.morph2 = () => {
    particles.morph(2);
  };
  particles.morph3 = () => {
    particles.morph(3);
  };

  gui.add(particles, "morph0");
  gui.add(particles, "morph1");
  gui.add(particles, "morph2");
  gui.add(particles, "morph3");
});

/**
 * Animate
 */
const tick = () => {
  // Update controls
  controls.update();

  /**
   * Raycaster
   */
  displacement.raycaster.setFromCamera(displacement.screenCursor, camera);
  const intersections = displacement.raycaster.intersectObject(displacement.interactivePlane);

  if (intersections.length) {
    const uv = intersections[0].uv;
    displacement.canvasCursor.x = uv.x * displacement.canvas.width;
    displacement.canvasCursor.y = (1 - uv.y) * displacement.canvas.height;
  }

  /**
   * Displacement
   */
  // Fade out
  displacement.context.globalCompositeOperation = "source-over";
  displacement.context.globalAlpha = 0.02;
  displacement.context.fillRect(0, 0, displacement.canvas.width, displacement.canvas.height);

  // Speed alpha
  const distanceCursor = displacement.canvasCursorPrevious.distanceTo(displacement.canvasCursor);
  displacement.canvasCursorPrevious.copy(displacement.canvasCursor);
  const alpha = Math.min(distanceCursor * 0.1, 1);

  // Draw glow
  displacement.context.globalCompositeOperation = "lighten";
  displacement.context.globalAlpha = alpha;
  const glowSize = displacement.canvas.width * 0.25;

  // Center the glow at the cursor coords
  displacement.context.drawImage(
    displacement.glowImage,
    displacement.canvasCursor.x - glowSize / 2,
    displacement.canvasCursor.y - glowSize / 2,
    glowSize,
    glowSize
  );

  displacement.texture.needsUpdate = true;

  // Render normal scene
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
